import React, { useEffect } from 'react';
import { Grid, TextField } from '@material-ui/core';
import {
  Dialog, DialogContent, DialogTitle, IconButton, Box,
  Fab, CircularProgress, InputLabel, FormControl, MenuItem, Select,
  FormLabel, FormGroup, FormControlLabel, Checkbox, FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import {
  Formik, Form, FormikProps, Field
} from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import _ from 'lodash';
import { Autocomplete } from 'formik-material-ui-lab';
import { AutocompleteRenderInputParams } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { useSnackbar } from 'notistack';
import { ScaleLoader } from 'react-spinners';

import UseStyles from '../../components/styles/loadingButton';
import { setAddressByCEP } from '../../services/geolocateService';
import { CepMask, CpfMask, PhoneMask } from '../../components/mask';
import Dictionary from '../../models/interfaces/dictionary';
import { getErrorMessage } from '../../config/firebase';
import Address from '../../models/interfaces/address';
import driverService from '../../services/driverService';
import CategoryDriver, { CategoryDriverList } from '../../models/enums/categoryDriver';
import { PaymentTypeList } from '../../models/enums/paymentType';
import { useAuth } from '../../contexts/auth';
import subsidiaryService from '../../services/subsidiaryService';
import authService from '../../services/authService';

interface Props {
  open: boolean;
  driverId: string | null;
  onClose: () => void;
  onSave: () => void;
}

interface Values {
  id: string;
  name: string;
  dtBirth?: Date;
  email: string;
  cpf: string;
  address: Address;
  phoneNumber: string | null;
  paymentTypes: number[];
  rg: string;
  category: CategoryDriver;
  comments: string;
  cloudMessageToken: string;
  subsidiary: any;
}

// Função para obter valores iniciais
const getInitialValues = (driverData: any, driverId: string | null): Values => ({
  id: driverId || driverData?.id || '',
  name: driverData?.name || '',
  dtBirth: driverData?.dtBirth || undefined,
  email: driverData?.email || '',
  cpf: driverData?.cpf || '',
  address: {
    zipCode: driverData?.address?.zipCode || '',
    street: driverData?.address?.street || '',
    number: driverData?.address?.number || '',
    complement: driverData?.address?.complement || '',
    neighborhood: driverData?.address?.neighborhood || '',
    city: driverData?.address?.city || '',
    state: driverData?.address?.state || null,
    ibge: driverData?.address?.ibge || null,
    location: null
  },
  phoneNumber: driverData?.phoneNumber || '',
  paymentTypes: driverData?.paymentTypes || [],
  rg: driverData?.rg || '',
  category: driverData?.category || CategoryDriver.Undefined,
  comments: driverData?.comments || '',
  cloudMessageToken: driverData?.cloudMessageToken || '',
  subsidiary: driverData?.subsidiary ? {
    key: driverData.subsidiary,
    value: driverData.subsidiaryName
  } : null
});

const DriverEditModal = ({
  open,
  driverId,
  onClose,
  onSave
}: Props) => {
  const classes = UseStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [success, setSuccess] = React.useState(false);
  const [subsidiaries, setSubsidiaries] = React.useState<Dictionary[]>();
  const { subsidiaryId } = useAuth();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [driverData, setDriverData] = React.useState<any>(null);
  const [key, setKey] = React.useState(0);

  const buttonClassname = clsx({ [classes.buttonSuccess]: success });

  const onBlurCep = (ev: any, setFieldValue: any): void => {
    setAddressByCEP(ev, setFieldValue).catch((e) => {
      enqueueSnackbar(e.message, { variant: 'warning' });
    });
  };

  const handleCheckbox = (event: any, list: number[], setFieldValue: any, fieldName: string) => {
    const optionId = parseFloat(event.target.value);
    let result = [...list];

    if (event.target.checked) {
      result.push(optionId);
    } else {
      result = result.filter((x) => x !== optionId);
    }
    setFieldValue(fieldName, result);
  };

  useEffect(() => {
    if (!subsidiaryId) {
      subsidiaryService.getByPaginationAsync(1, 200).then((result) => {
        if (!result.data.length) throw new Error('Nenhuma filial encontrada');
        setSubsidiaries(result.data.map((x: any) => ({ key: x.id, value: x.description })));
      });
    }
  }, [subsidiaryId]);

  useEffect(() => {
    if (!subsidiaries?.length) return;
    if (!driverData?.subsidiary) return;
    if (driverData?.subsidiaryName) return;

    const found = subsidiaries.find((s) => s.key === driverData.subsidiary);
    if (!found?.value) return;

    setDriverData((prev: any) => ({ ...prev, subsidiaryName: found.value }));
  }, [subsidiaries, driverData?.subsidiary, driverData?.subsidiaryName]);

  useEffect(() => {
    if (open && driverId) {
      setIsLoading(true);
      driverService.getById(driverId).then(async (item: any) => {
        setDriverData({ ...item, id: driverId });
        setIsLoading(false);
        setKey((prev) => prev + 1);
      }).catch(() => {
        enqueueSnackbar('Motorista não encontrado', { variant: 'error' });
        onClose();
        setIsLoading(false);
      });
    } else if (open && !driverId) {
      setDriverData(null);
      setIsLoading(false);
      setKey((prev) => prev + 1);
    }
  }, [driverId, open, onClose, enqueueSnackbar]);

  const handleSubmit = async (values: Values, actions: any) => {
    try {
      await authService.updateEmailPhoneNumber(values.email, values.phoneNumber!, driverId!);
      await driverService.update({
        name: values.name,
        cpf: values.cpf,
        dtBirth: values.dtBirth!,
        email: values.email,
        address: values.address,
        phoneNumber: values.phoneNumber!,
        category: values.category,
        rg: values.rg,
        comments: values.comments,
        paymentTypes: values.paymentTypes ?? [],
        cloudMessageToken: values.cloudMessageToken,
        subsidiary: values.subsidiary?.key ?? subsidiaryId
      }, values.id);

      setSuccess(true);
      onSave();

      setTimeout(() => {
        onClose();
      }, 500);
    } catch (e) {
      setSuccess(false);
      if (e instanceof Error && e.message === 'auth/phone-number-already-exists') {
        enqueueSnackbar('Não foi possível atualizar o número de telefone. Número já esta sendo usado em outra conta.', { variant: 'warning' });
      } else if (e instanceof Error && e.message === 'auth/email-already-exists') {
        enqueueSnackbar('Não foi possível atualizar o email. Este email já esta sendo usado em outra conta.', { variant: 'warning' });
      } else {
        enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      fullScreen={false}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>{driverId ? 'Editar Motorista' : 'Novo Motorista'}</span>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: '360px' }}>
            <ScaleLoader color="var(--primary)" loading />
          </div>
        ) : (
          <Formik
            key={key}
            initialValues={getInitialValues(driverData, driverId)}
            validationSchema={Yup.object().shape({
              name: Yup.string().required(),
              category: Yup.number().required().positive(),
              cpf: Yup.string().required(),
              email: Yup.string().email().required(),
              subsidiary: Yup.lazy(() => {
                if (subsidiaryId) return Yup.object().nullable();
                return Yup.object().nullable().required();
              }),
              phoneNumber: Yup.string().required()
                .matches(/^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/, 'Telefone inválido'),
              address: Yup.object().shape({
                zipCode: Yup.string().required(),
                street: Yup.string().required(),
                neighborhood: Yup.string().required(),
                city: Yup.string().required(),
                number: Yup.string().required()
              })
            })}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(props: FormikProps<Values>) => {
              const {
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
                setFieldValue
              } = props;

              return (
                <Form noValidate autoComplete="off">
                  <Field type="hidden" name="address.ibge" value="" />
                  <Grid container spacing={3}>
                    {subsidiaries?.length && !subsidiaryId && (
                      <Grid item xs={12} sm={12} md={4}>
                        <Field
                          name="subsidiary"
                          component={Autocomplete}
                          options={subsidiaries}
                          getOptionLabel={(option: Dictionary | string) => {
                            if (typeof option === 'string') return option;
                            return option?.value ?? option?.key?.toString() ?? '';
                          }}
                          getOptionSelected={(option: Dictionary, value: Dictionary) => option?.key === value?.key}
                          renderInput={(params: AutocompleteRenderInputParams) => (
                            <TextField
                              {...params}
                              fullWidth
                              required
                              variant="outlined"
                              label="Franquia"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={Boolean(errors.subsidiary && touched.subsidiary)}
                              helperText={errors.subsidiary && touched.subsidiary && errors.subsidiary}
                            />
                          )}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={12} md={8}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Nome"
                        name="name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoFocus
                        value={values.name}
                        error={Boolean(errors.name && touched.name)}
                        helperText={errors.name && touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel>Categoria</InputLabel>
                        <Field
                          as={Select}
                          required
                          label="Categoria"
                          name="category"
                          value={values.category}
                          error={Boolean(errors.category && touched.category)}
                        >
                          {CategoryDriverList.map((item: any) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="CPF"
                        name="cpf"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.cpf}
                        error={Boolean(errors.cpf && touched.cpf)}
                        helperText={errors.cpf && touched.cpf && errors.cpf}
                        InputProps={{ inputComponent: CpfMask as any }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <KeyboardDatePicker
                        fullWidth
                        required
                        inputVariant="outlined"
                        label="Data de Nascimento"
                        name="dtBirth"
                        InputLabelProps={{ shrink: true }}
                        format="dd/MM/yyyy"
                        value={values.dtBirth}
                        onChange={(date: Date | null) => { setFieldValue('dtBirth', date); }}
                        onBlur={handleBlur}
                        error={Boolean(errors.dtBirth && touched.dtBirth)}
                        helperText={errors.dtBirth && touched.dtBirth && errors.dtBirth}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        label="RG"
                        name="rg"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.rg}
                        error={Boolean(errors.rg && touched.rg)}
                        helperText={errors.rg && touched.rg && errors.rg}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={8}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Email"
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        error={Boolean(errors.email && touched.email)}
                        helperText={errors.email && touched.email && errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="CEP"
                        name="address.zipCode"
                        onChange={handleChange}
                        onBlur={(e: any) => onBlurCep(e, setFieldValue)}
                        value={values.address.zipCode}
                        error={Boolean(errors.address?.zipCode && touched.address?.zipCode)}
                        helperText={errors.address?.zipCode && touched.address?.zipCode && errors.address?.zipCode}
                        InputProps={{ inputComponent: CepMask as any }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={8}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Endereço"
                        name="address.street"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.street}
                        error={Boolean(errors.address?.street && touched.address?.street)}
                        helperText={errors.address?.street && touched.address?.street && errors.address?.street}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Bairro"
                        name="address.neighborhood"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.neighborhood}
                        error={Boolean(errors.address?.neighborhood && touched.address?.neighborhood)}
                        helperText={errors.address?.neighborhood && touched.address?.neighborhood && errors.address?.neighborhood}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Cidade"
                        name="address.city"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.city}
                        error={Boolean(errors.address?.city && touched.address?.city)}
                        helperText={errors.address?.city && touched.address?.city && errors.address?.city}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Nº"
                        name="address.number"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.number}
                        error={Boolean(errors.address?.number && touched.address?.number)}
                        helperText={errors.address?.number && touched.address?.number && errors.address?.number}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        label="Complemento"
                        name="address.complement"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address.complement}
                        error={Boolean(errors.address?.complement && touched.address?.complement)}
                        helperText={errors.address?.complement && touched.address?.complement && errors.address?.complement}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        required
                        variant="outlined"
                        label="Telefone"
                        name="phoneNumber"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phoneNumber}
                        error={Boolean(errors.phoneNumber && touched.phoneNumber)}
                        helperText={errors.phoneNumber && touched.phoneNumber && errors.phoneNumber}
                        InputProps={{ inputComponent: PhoneMask as any }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Chamadas por tipo de pagamento</FormLabel>
                        <FormHelperText sx={{ mb: 1 }}>
                          Deixe vazio para o condutor receber todas. Selecione para limitar às chamadas com os tipos escolhidos.
                        </FormHelperText>
                        <FormGroup>
                          {_.sortBy(PaymentTypeList, [(x) => x.value]).map((item: Dictionary) => (
                            <FormControlLabel
                              key={item.key}
                              value={item.key}
                              control={(
                                <Checkbox
                                  checked={values.paymentTypes?.includes(item.key)}
                                  onChange={(e) => handleCheckbox(e, values.paymentTypes ?? [], setFieldValue, 'paymentTypes')}
                                  name="paymentTypes"
                                />
                              )}
                              label={item.value}
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        variant="outlined"
                        label="Observações"
                        name="comments"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.comments}
                        error={Boolean(errors.comments && touched.comments)}
                        helperText={errors.comments && touched.comments && errors.comments}
                        multiline
                        minRows="4"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container justifyContent="flex-end" spacing={3}>
                        <div className={classes.wrapper}>
                          <Fab
                            aria-label="save"
                            color="primary"
                            className={buttonClassname}
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {success ? <CheckIcon /> : <SaveIcon />}
                          </Fab>
                          {isSubmitting && (
                            <CircularProgress size={68} className={classes.fabProgress} />
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriverEditModal;
